import React, { useState } from 'react';
import FieldBrowser from './FieldBrowser';
import FilterBuilder from './FilterBuilder';
import { ArrowUp, ArrowDown, X, GripVertical, ArrowUpDown, Hash } from 'lucide-react';

const TableEditor = ({ entityName, config, onChange }) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleFieldToggle = (field) => {
    const currentFields = config.fields || [];
    const fieldExists = currentFields.some(f => f.name === field.name);

    if (fieldExists) {
      // Remove field
      onChange({
        ...config,
        fields: currentFields.filter(f => f.name !== field.name)
      });
    } else {
      // Add field with default format based on type
      const numericTypes = ['integer', 'decimal', 'number', 'int', 'float', 'double', 'bigint', 'currency'];
      const dateTypes = ['date', 'datetime', 'timestamp'];
      const isNumeric = numericTypes.includes(field.type?.toLowerCase());
      const isDate = dateTypes.includes(field.type?.toLowerCase());
      
      const newField = {
        name: field.name,
        label: field.label,
        type: field.type
      };
      
      // Set default format based on type
      if (isNumeric) {
        newField.format = 'number'; // Default to 'number' format
      } else if (isDate) {
        newField.format = 'date'; // Default to 'date' format
      }
      
      onChange({
        ...config,
        fields: [...currentFields, newField]
      });
    }
  };
  
  const handleFieldUpdate = (index, key, value) => {
    const currentFields = [...(config.fields || [])];
    currentFields[index] = {
      ...currentFields[index],
      [key]: value
    };
    onChange({
      ...config,
      fields: currentFields
    });
  };
  
  const isNumericField = (type) => {
    const numericTypes = ['integer', 'decimal', 'number', 'int', 'float', 'double', 'bigint', 'currency'];
    return numericTypes.includes(type?.toLowerCase());
  };
  
  const isDateField = (type) => {
    const dateTypes = ['date', 'datetime', 'timestamp'];
    return dateTypes.includes(type?.toLowerCase());
  };

  const handleMoveField = (index, direction) => {
    const currentFields = [...(config.fields || [])];
    const newIndex = index + direction;

    if (newIndex >= 0 && newIndex < currentFields.length) {
      const [movedField] = currentFields.splice(index, 1);
      currentFields.splice(newIndex, 0, movedField);

      onChange({
        ...config,
        fields: currentFields
      });
    }
  };

  const handleRemoveField = (index) => {
    const currentFields = [...(config.fields || [])];
    currentFields.splice(index, 1);

    onChange({
      ...config,
      fields: currentFields
    });
  };

  // Drag and Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const currentFields = [...(config.fields || [])];
    const [draggedField] = currentFields.splice(draggedIndex, 1);
    currentFields.splice(dropIndex, 0, draggedField);

    onChange({
      ...config,
      fields: currentFields
    });

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="space-y-4">
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Table Configuration
        </h3>

        {/* Field Browser */}
        <FieldBrowser
          entityName={entityName}
          selectedFields={config.fields || []}
          onFieldToggle={handleFieldToggle}
        />

        {/* Selected Fields */}
        {config.fields && config.fields.length > 0 && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Selected Fields ({config.fields.length})
            </label>
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Drag to reorder or use arrow buttons
            </div>
            <div className="space-y-2">
              {config.fields.map((field, index) => (
                <div
                  key={field.name}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 transition-all cursor-move ${
                    draggedIndex === index
                      ? 'border-teal-500 opacity-50 scale-95'
                      : dragOverIndex === index
                      ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {/* Drag Handle */}
                  <div className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-grab active:cursor-grabbing">
                    <GripVertical className="w-4 h-4" />
                  </div>

                  {/* Field Info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {field.label}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {field.name} • {field.type}
                    </div>
                    {/* Format Options */}
                    {isNumericField(field.type) && (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Number Format
                        </label>
                        <select
                          value={field.format || 'number'}
                          onChange={(e) => handleFieldUpdate(index, 'format', e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                        >
                          <option value="number">Number (1,234.56)</option>
                          <option value="currency">Currency ($1,234.56)</option>
                          <option value="decimal">Decimal (2 decimals)</option>
                          <option value="integer">Integer (1,234)</option>
                          <option value="percentage">Percentage (12.34%)</option>
                        </select>
                        {field.format === 'currency' && (
                          <select
                            value={field.currency || 'USD'}
                            onChange={(e) => handleFieldUpdate(index, 'currency', e.target.value)}
                            className="w-full mt-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                          >
                            <option value="USD">USD ($)</option>
                            <option value="EUR">EUR (€)</option>
                            <option value="GBP">GBP (£)</option>
                            <option value="JPY">JPY (¥)</option>
                            <option value="THB">THB (฿)</option>
                          </select>
                        )}
                      </div>
                    )}
                    {isDateField(field.type) && (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Date Format
                        </label>
                        <select
                          value={field.format || 'date'}
                          onChange={(e) => handleFieldUpdate(index, 'format', e.target.value)}
                          className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-teal-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                        >
                          <option value="date">Date (MM/DD/YYYY)</option>
                          <option value="datetime">Date & Time (MM/DD/YYYY HH:mm)</option>
                          <option value="iso">ISO (YYYY-MM-DD)</option>
                          <option value="iso-datetime">ISO DateTime (YYYY-MM-DD HH:mm:ss)</option>
                          <option value="relative">Relative (2 days ago)</option>
                          <option value="long">Long (January 1, 2025)</option>
                          <option value="short">Short (1/1/25)</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleMoveField(index, -1)}
                      disabled={index === 0}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Move up"
                    >
                      <ArrowUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleMoveField(index, 1)}
                      disabled={index === config.fields.length - 1}
                      className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Move down"
                    >
                      <ArrowDown className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleRemoveField(index)}
                      className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 transition-colors"
                      title="Remove"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sorting Configuration */}
        {config.fields && config.fields.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              <ArrowUpDown className="w-4 h-4 inline mr-2" />
              Default Sorting
            </label>
            <div className="grid grid-cols-2 gap-3">
              {/* Sort Field */}
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Sort By Field
                </label>
                <select
                  value={config.sort?.field || ''}
                  onChange={(e) => onChange({
                    ...config,
                    sort: {
                      ...config.sort,
                      field: e.target.value
                    }
                  })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="">No sorting</option>
                  {config.fields.map(field => (
                    <option key={field.name} value={field.name}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort Direction */}
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  Direction
                </label>
                <select
                  value={config.sort?.direction || 'asc'}
                  onChange={(e) => onChange({
                    ...config,
                    sort: {
                      ...config.sort,
                      direction: e.target.value
                    }
                  })}
                  disabled={!config.sort?.field}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="asc">Ascending (A-Z, 0-9)</option>
                  <option value="desc">Descending (Z-A, 9-0)</option>
                </select>
              </div>
            </div>
            {config.sort?.field && (
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Results will be sorted by <span className="font-medium">{config.fields.find(f => f.name === config.sort.field)?.label}</span> in <span className="font-medium">{config.sort.direction === 'asc' ? 'ascending' : 'descending'}</span> order
              </div>
            )}
          </div>
        )}

        {/* Max Records Configuration */}
        {config.fields && config.fields.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Hash className="w-4 h-4 inline mr-2" />
              Maximum Records
            </label>
            <div className="flex items-center gap-3">
              <input
                type="number"
                value={config.maxRecords || 100}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 100;
                  onChange({
                    ...config,
                    maxRecords: Math.min(Math.max(value, 1), 10000) // Min 1, Max 10000
                  });
                }}
                min="1"
                max="10000"
                className="w-32 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-gray-100"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                records per page
              </span>
            </div>
            <div className="mt-2 flex items-start gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ℹ️ Limit: 1-10,000 records. Default is 100. Use pagination for more results.
              </span>
            </div>
          </div>
        )}

        {/* Filters */}
        {config.fields && config.fields.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <FilterBuilder
              entityName={entityName}
              filters={config.filters || []}
              onChange={(filters) => onChange({ ...config, filters })}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TableEditor;

